import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

import { TimerProvider } from "@/contexts/TimerContext";
import { EntitlementsProvider } from "@/contexts/EntitlementsContext";
import { Suspense, useEffect } from "react";
import { OrphanedSessionHandler } from "@/components/OrphanedSessionHandler";
import { lazyWithRetry, initChunkErrorHandling } from "@/lib/lazyWithRetry";
import { HelmetProvider } from "react-helmet-async";

// Lazy load pages for code splitting with retry logic for chunk loading errors
const Index = lazyWithRetry(() => import("./pages/Index"), "Index");
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"), "Dashboard");
const Analytics = lazyWithRetry(() => import("./pages/Analytics"), "Analytics");
const Session = lazyWithRetry(() => import("./pages/Session"), "Session");
const Join = lazyWithRetry(() => import("./pages/Join"), "Join");
const GreenRoom = lazyWithRetry(() => import("./pages/GreenRoom"), "GreenRoom");
const Auth = lazyWithRetry(() => import("./pages/Auth"), "Auth");
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"), "Onboarding");
const NotFound = lazyWithRetry(() => import("./pages/NotFound"), "NotFound");
const Planner = lazyWithRetry(() => import("./pages/Planner"), "Planner");
const DoWork = lazyWithRetry(() => import("./pages/DoWork"), "DoWork");
const Team = lazyWithRetry(() => import("./pages/Team"), "Team");
const FocusRoomsPage = lazyWithRetry(() => import("./components/focus-rooms/FocusRoomsPage"), "FocusRoomsPage");
const Settings = lazyWithRetry(() => import("./pages/Settings"), "Settings");
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"), "AuthCallback");
const MigrationStatus = lazyWithRetry(() => import("./pages/MigrationStatus"), "MigrationStatus");
const SecurityMonitoring = lazyWithRetry(() => import("./components/admin/SecurityMonitoringDashboard"), "SecurityMonitoring");
const CalendarSyncManager = lazyWithRetry(() => import("./components/calendar/CalendarSyncManager"), "CalendarSyncManager");
const PerformanceAnalytics = lazyWithRetry(() => import("./components/analytics/PerformanceAnalyticsDashboard"), "PerformanceAnalytics");
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"), "TermsOfService");
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"), "PrivacyPolicy");
const Contact = lazyWithRetry(() => import("./pages/Contact"), "Contact");

const ContentHub = lazyWithRetry(() => import("./pages/ContentHub"), "ContentHub");
const HubsDirectory = lazyWithRetry(() => import("./pages/hub/HubsDirectory"), "HubsDirectory");
const ExecutionHub = lazyWithRetry(() => import("./pages/hub/execution/index"), "ExecutionHub");
const PlanningFallacy = lazyWithRetry(() => import("./pages/hub/execution/planning-fallacy"), "PlanningFallacy");
const ActiveVsPassive = lazyWithRetry(() => import("./pages/hub/execution/active-vs-passive"), "ActiveVsPassive");
const ContextSwitching = lazyWithRetry(() => import("./pages/hub/execution/context-switching"), "ContextSwitching");
const MicroDeadlines = lazyWithRetry(() => import("./pages/hub/execution/micro-deadlines"), "MicroDeadlines");
const Timeboxing = lazyWithRetry(() => import("./pages/hub/execution/timeboxing"), "Timeboxing");
const TimeBlocking = lazyWithRetry(() => import("./pages/hub/execution/time-blocking"), "TimeBlocking");
const PomodoroGuide = lazyWithRetry(() => import("./pages/hub/execution/pomodoro-guide"), "PomodoroGuide");
const TaskBatching = lazyWithRetry(() => import("./pages/hub/execution/task-batching"), "TaskBatching");
const DeepWorkGuide = lazyWithRetry(() => import("./pages/hub/execution/deep-work-guide"), "DeepWorkGuide");
const EatTheFrogGuide = lazyWithRetry(() => import("./pages/hub/execution/eat-the-frog"), "EatTheFrogGuide");
const WhatIsDeepFocus = lazyWithRetry(() => import("./pages/hub/execution/what-is-deep-focus"), "WhatIsDeepFocus");

// Hub 2: Neurodivergent Workspace
const NeurodivergentHub = lazyWithRetry(() => import("./pages/hub/neurodivergent/index"), "NeurodivergentHub");
const DopamineMenu = lazyWithRetry(() => import("./pages/hub/neurodivergent/dopamine-menu"), "DopamineMenu");
const BodyDoubling = lazyWithRetry(() => import("./pages/hub/neurodivergent/body-doubling"), "BodyDoubling");
const TimeBlindness = lazyWithRetry(() => import("./pages/hub/neurodivergent/time-blindness"), "TimeBlindness");
const WaitingMode = lazyWithRetry(() => import("./pages/hub/neurodivergent/waiting-mode"), "WaitingMode");
const AdhdParalysis = lazyWithRetry(() => import("./pages/hub/neurodivergent/adhd-paralysis"), "AdhdParalysis");
const HowToFocusAdhd = lazyWithRetry(() => import("./pages/hub/neurodivergent/how-to-focus-adhd"), "HowToFocusAdhd");
const AdhdTimeManagement = lazyWithRetry(() => import("./pages/hub/neurodivergent/adhd-time-management"), "AdhdTimeManagement");
const Hyperfocus = lazyWithRetry(() => import("./pages/hub/neurodivergent/hyperfocus"), "Hyperfocus");
const AdhdProcrastination = lazyWithRetry(() => import("./pages/hub/neurodivergent/adhd-procrastination"), "AdhdProcrastination");

// Hub 3: Quantified Performance
const QuantifiedHub = lazyWithRetry(() => import("./pages/hub/quantified/index"), "QuantifiedHub");
const DeepWorkRatio = lazyWithRetry(() => import("./pages/hub/quantified/deep-work-ratio"), "DeepWorkRatio");
const UltradianRhythms = lazyWithRetry(() => import("./pages/hub/quantified/ultradian-rhythms"), "UltradianRhythms");
const FlowState = lazyWithRetry(() => import("./pages/hub/quantified/flow-state"), "FlowState");
const VelocityVariance = lazyWithRetry(() => import("./pages/hub/quantified/velocity-variance"), "VelocityVariance");
const ChronotypeProductivity = lazyWithRetry(() => import("./pages/hub/quantified/chronotype-productivity"), "ChronotypeProductivity");
const EnergyManagement = lazyWithRetry(() => import("./pages/hub/quantified/energy-management"), "EnergyManagement");
const ProductivityMetrics = lazyWithRetry(() => import("./pages/hub/quantified/productivity-metrics"), "ProductivityMetrics");

// SEO programmatic pages
const TimerHubPage = lazyWithRetry(
  () => import("./pages/seo/TimerHubPage"),
  "TimerHubPage"
);
const TimerProtocolPage = lazyWithRetry(
  () => import("./pages/seo/TimerProtocolPage"),
  "TimerProtocolPage"
);
const PersonaPage = lazyWithRetry(
  () => import("./pages/seo/PersonaPage"),
  "PersonaPage"
);
const ToolPage = lazyWithRetry(
  () => import("./pages/seo/ToolPage"),
  "ToolPage"
);
const IntegrationPage = lazyWithRetry(
  () => import("./pages/seo/IntegrationPage"),
  "IntegrationPage"
);

const queryClient = new QueryClient();

const App = () => {
  // Initialize chunk error handling on mount
  useEffect(() => {
    initChunkErrorHandling();
  }, []);

  return (
    <ErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <TimerProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <EntitlementsProvider>
                    <OrphanedSessionHandler>
                      <Suspense fallback={
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
                        </div>
                      }>
                        <Routes>
                        <Route path="/" element={<Index />} />
                        {/* Hubs Directory */}
                        <Route path="/hubs" element={<HubsDirectory />} />
                        {/* Hub 1: Execution Lab */}
                        <Route path="/hub/execution" element={<ExecutionHub />} />
                        <Route path="/hub/execution/planning-fallacy" element={<PlanningFallacy />} />
                        <Route path="/hub/execution/active-vs-passive" element={<ActiveVsPassive />} />
                        <Route path="/hub/execution/context-switching" element={<ContextSwitching />} />
                        <Route path="/hub/execution/micro-deadlines" element={<MicroDeadlines />} />
                        <Route path="/hub/execution/timeboxing" element={<Timeboxing />} />
                        <Route path="/hub/execution/time-blocking" element={<TimeBlocking />} />
                        <Route path="/hub/execution/pomodoro-guide" element={<PomodoroGuide />} />
                        <Route path="/hub/execution/task-batching" element={<TaskBatching />} />
                        <Route path="/hub/execution/deep-work-guide" element={<DeepWorkGuide />} />
                        <Route path="/hub/execution/eat-the-frog" element={<EatTheFrogGuide />} />
                        <Route path="/hub/execution/what-is-deep-focus" element={<WhatIsDeepFocus />} />
                        {/* Hub 2: Neurodivergent Workspace */}
                        <Route path="/hub/neurodivergent" element={<NeurodivergentHub />} />
                        <Route path="/hub/neurodivergent/dopamine-menu" element={<DopamineMenu />} />
                        <Route path="/hub/neurodivergent/body-doubling" element={<BodyDoubling />} />
                        <Route path="/hub/neurodivergent/time-blindness" element={<TimeBlindness />} />
                        <Route path="/hub/neurodivergent/waiting-mode" element={<WaitingMode />} />
                        <Route path="/hub/neurodivergent/adhd-paralysis" element={<AdhdParalysis />} />
                        <Route path="/hub/neurodivergent/how-to-focus-adhd" element={<HowToFocusAdhd />} />
                        <Route path="/hub/neurodivergent/adhd-time-management" element={<AdhdTimeManagement />} />
                        <Route path="/hub/neurodivergent/hyperfocus" element={<Hyperfocus />} />
                        <Route path="/hub/neurodivergent/adhd-procrastination" element={<AdhdProcrastination />} />
                        {/* Hub 3: Quantified Performance */}
                        <Route path="/hub/quantified" element={<QuantifiedHub />} />
                        <Route path="/hub/quantified/deep-work-ratio" element={<DeepWorkRatio />} />
                        <Route path="/hub/quantified/ultradian-rhythms" element={<UltradianRhythms />} />
                        <Route path="/hub/quantified/flow-state" element={<FlowState />} />
                        <Route path="/hub/quantified/velocity-variance" element={<VelocityVariance />} />
                        <Route path="/hub/quantified/chronotype-productivity" element={<ChronotypeProductivity />} />
                        <Route path="/hub/quantified/energy-management" element={<EnergyManagement />} />
                        <Route path="/hub/quantified/productivity-metrics" element={<ProductivityMetrics />} />
                        {/* Still keep generic hub route for others for now */}
                        <Route path="/hub/:hubId" element={<ContentHub />} />
                        {/* Programmatic SEO routes */}
                        <Route path="/timer" element={<TimerHubPage />} />
                        <Route path="/timer/:protocol" element={<TimerProtocolPage />} />
                        <Route path="/for/:persona" element={<PersonaPage />} />
                        <Route path="/tools/:tool" element={<ToolPage />} />
                        <Route path="/integrations/:service" element={<IntegrationPage />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/do-work" element={<DoWork />} />
                        <Route path="/team-roles" element={<Team />} />
                        <Route path="/session/:id" element={<Session />} />
                        <Route path="/greenroom/:id" element={<GreenRoom />} />
                        <Route path="/join/:code" element={<Join />} />
                        <Route path="/join" element={<Join />} />
                        <Route path="/login" element={<Navigate to="/auth" replace />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/onboarding" element={<Onboarding />} />
                        <Route path="/planner" element={<Planner />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/migration-status" element={<MigrationStatus />} />
                        <Route path="/admin/security" element={<SecurityMonitoring />} />
                        <Route path="/calendar/sync" element={<CalendarSyncManager />} />
                        <Route path="/admin/performance" element={<PerformanceAnalytics />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </OrphanedSessionHandler>
                  </EntitlementsProvider>
                </BrowserRouter>
              </TimerProvider>
            </TooltipProvider>
          </QueryClientProvider>
        </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
